/**
 * 사진찍기 인사 화면.
 * 촬영 안내 영상의 첫 프레임(굴)을 3초간 정지 표시하고 이름·안내 문구를 얹는다.
 * 인사말 종료 후 같은 video를 유지한 채 문구만 제거하고 재생한다.
 */
import React, { useEffect, useRef, useState } from 'react';
import useQuestVideoWatchdog from '../../hooks/useQuestVideoWatchdog';
import './OxQuizFlow.css';
import './FunnyPhotoMissionFlow.css';

export default function FunnyPhotoGreetingScreen({
  memberNickname, guideVideoUrl, playing = false, onFinished, onAbort,
  onGreetingReady, onGreetingAbort, onCancel,
}) {
  const canvasRef = useRef(null);
  const [frameReady, setFrameReady] = useState(false);
  const greetingCallbacks = useRef({ onGreetingReady, onGreetingAbort });
  greetingCallbacks.current = { onGreetingReady, onGreetingAbort };
  const { videoRef, videoHandlers, videoPlaying } = useQuestVideoWatchdog({
    active: playing && !!guideVideoUrl,
    videoUrl: guideVideoUrl,
    onFinished,
    onAbort,
    logLabel: '사진찍기 촬영 안내 영상',
  });
  const { onError: handleVideoError } = videoHandlers;

  useEffect(() => {
    if (playing || !guideVideoUrl) return undefined;
    const video = videoRef.current;
    let settled = false;
    let frameId;
    setFrameReady(false);
    const fail = reason => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      video.pause();
      greetingCallbacks.current.onGreetingAbort?.(reason);
    };
    const capture = () => {
      if (settled || video.readyState < 2 || !video.videoWidth || !video.videoHeight) return;
      try {
        const canvas = canvasRef.current;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        // 픽셀 읽기나 저장 없이 화면에만 표시하므로 다른 출처의 영상도 그릴 수 있다.
        canvas.getContext('2d').drawImage(video, 0, 0);
        video.pause();
        settled = true;
        clearTimeout(timeout);
        setFrameReady(true);
        greetingCallbacks.current.onGreetingReady?.();
      } catch { fail('error'); }
    };
    const timeout = setTimeout(() => fail('load_timeout'), 10000);
    const error = () => fail('error');
    video.addEventListener('error', error);
    // 화면 밖 사전 준비에서는 프레임 표시 콜백이 지연될 수 있다.
    // 디코딩된 첫 프레임이 있으면 loadeddata에서도 canvas를 준비한다.
    video.addEventListener('loadeddata', capture);
    // Android의 재생 전 회색 표면을 피하기 위해 무음으로 첫 프레임을 디코딩한다.
    // canvas로 정지 화면을 유지하므로 pause 후 네이티브 재생 UI도 노출되지 않는다.
    if (video.requestVideoFrameCallback) frameId = video.requestVideoFrameCallback(capture);
    else video.addEventListener('playing', capture);
    video.muted = true;
    try { video.play()?.catch(() => fail('error')); } catch { fail('error'); }
    return () => {
      settled = true;
      clearTimeout(timeout);
      if (frameId !== undefined) video.cancelVideoFrameCallback?.(frameId);
      video.removeEventListener('playing', capture);
      video.removeEventListener('error', error);
      video.removeEventListener('loadeddata', capture);
      video.pause();
    };
  }, [playing, guideVideoUrl, videoRef]);

  useEffect(() => {
    const video = videoRef.current;
    if (!playing || !video) return undefined;
    let cancelled = false;
    // 인사말 중 발생한 로드 오류도 재생 단계에서 기존 이탈 경로로 처리한다.
    if (video.error) {
      handleVideoError();
      return undefined;
    }
    try {
      const request = video.play();
      request?.catch(() => {
        if (!cancelled) handleVideoError();
      });
    } catch {
      handleVideoError();
    }
    return () => { cancelled = true; };
  }, [playing, guideVideoUrl, videoRef, handleVideoError]);

  const displayName = typeof memberNickname === 'string' && memberNickname.trim()
    ? memberNickname.trim()
    : '주렁 탐험가';
  return (
    <div className="oxq-screen fp-greet" data-testid="funny-photo-greeting-screen">
      {guideVideoUrl && (
        <video
          ref={videoRef}
          className="fp-greet-guide-still"
          style={{ opacity: playing && videoPlaying ? 1 : 0 }}
          src={guideVideoUrl}
          preload="auto"
          muted={!playing}
          playsInline
          aria-hidden={!playing}
          aria-label={playing ? '사진찍기 미션 촬영 안내 영상' : undefined}
          data-testid="funny-photo-guide-still"
          {...(playing ? videoHandlers : {})}
        />
      )}
      {guideVideoUrl && <canvas ref={canvasRef} className="fp-greet-guide-still" aria-hidden="true" style={{ opacity: frameReady && !(playing && videoPlaying) ? 1 : 0 }} />}
      {!playing && guideVideoUrl && !frameReady && <div className="fp-greet-loading" role="status">
        <span>영상을 준비하고 있어요</span>
        {onCancel && <button type="button" onClick={onCancel}>대기 화면으로</button>}
      </div>}
      {!playing && (frameReady || !guideVideoUrl) && <div className="fp-greet-copy">
        <p className="fp-greet-name" data-testid="funny-photo-greeting-name">
          <span>{`{${displayName}}`}</span>
          {' 반가워!'}
        </p>
        <p className="fp-greet-lines" data-testid="funny-photo-greeting-lines">
          나를 보고 잘 따라해봐!
        </p>
      </div>}
    </div>
  );
}
